import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ChevronDownIcon, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { SearchApi } from '@/services/core/searchApi'
import type { ReportRef } from '@/services/reports/reportTemplatesApi'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'

interface ReportObjectSelectProps {
  /** Search API type to look through, e.g. `LineItem`. */
  searchType: string
  value: ReportRef | null
  onChange: (value: ReportRef | null) => void
  placeholder: string
  invalid?: boolean
  disabled?: boolean
}

/**
 * Picks the one object a report is about. Networks hold far too many line
 * items and ad units to load up front, so the list is the API's search
 * results for what has been typed.
 */
export function ReportObjectSelect({
  searchType,
  value,
  onChange,
  placeholder,
  invalid,
  disabled,
}: ReportObjectSelectProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const debouncedTerm = useDebouncedValue(term)

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['report-object-search', searchType, debouncedTerm],
    queryFn: async () =>
      (await SearchApi.search({ q: debouncedTerm, types: searchType, pageSize: 50 })).searchResults,
    enabled: open,
    staleTime: 60 * 1000,
  })

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="field"
          disabled={disabled}
          aria-invalid={invalid}
          className="w-full min-w-0 justify-between px-3"
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value ? value.name || value.id : placeholder}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-72 p-0">
        {/* The API does the matching, so cmdk must not filter as well. */}
        <Command shouldFilter={false}>
          <CommandInput value={term} onValueChange={setTerm} placeholder={t('common.search')} />
          <CommandList>
            {isFetching && (
              <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                {t('common.loading')}
              </div>
            )}
            {!isFetching && <CommandEmpty>{t('common.noResults')}</CommandEmpty>}
            {results.map((item) => (
              <CommandItem
                key={item.id}
                value={item.id}
                onSelect={() => {
                  onChange({ id: item.id, name: item.name })
                  setOpen(false)
                }}
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{item.name || item.id}</span>
                  {item.summary && (
                    <span className="truncate text-xs text-muted-foreground">{item.summary}</span>
                  )}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
